<section class="mock-test-section" id="mock-tests" [class.dashboard-mode]="isDashboard">
  <div class="mock-test-container">
    <!-- Header Area -->
    <div class="flex items-center justify-between gap-4 mb-8">
      <div>
        <h2 class="mock-test-title">
          Practice <span class="brand-gradient-text">Mock Test</span>
        </h2>
        <p class="mock-test-subtitle">
          Industry-aligned online mock tests to sharpen your skills and beat
          placement exams.
        </p>
      </div>
      <button
        class="btn-view-all"
        [routerLink]="isDashboard ? '/student-dashboard/placement-prep' : '/mock-test'"
        data-testid="mock-test-view-all"
      >
        <span>View all</span>
        <svg
          aria-hidden="true"
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2.5"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5 12h14"></path>
          <path d="m12 5 7 7-7 7"></path>
        </svg>
      </button>
    </div>

    <!-- Cards Scroll Container -->
    <div class="relative group">
      <!-- Left Scroll Arrow -->
      <button
        (click)="scrollLeft()"
        *ngIf="!isAtStart && mockTest.length > 0"
        class="arrow-btn absolute -left-6 lg:-left-12 top-1/2 -translate-y-1/2 z-10"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2.5"
          viewBox="0 0 24 24"
        >
          <path d="M15 19l-7-7 7-7"></path>
        </svg>
      </button>

      <!-- Grid of Mock Test Cards -->
      <div class="mock-test-grid" #testsGrid (scroll)="onGridScroll()">
        <!-- Loading State -->
        <ng-container *ngIf="isLoading">
          <div
            class="mock-test-card loading-card-wrapper w-[95vw] sm:w-[50vw] md:w-full min-w-full flex justify-center items-center py-20 border-none shadow-none bg-transparent"
          >
            <div
              class="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"
            ></div>
          </div>
        </ng-container>

        <!-- Empty State -->
        <ng-container *ngIf="!isLoading && mockTest.length === 0">
          <div
            class="mock-test-card empty-card-wrapper w-[95vw] sm:w-[50vw] md:w-full min-w-full flex justify-center items-center py-20 border-none shadow-none bg-transparent"
          >
            <div class="card h-100 p-4 w-full max-w-md mx-auto text-center"
              style="border-radius: 12px; border: 1px solid #e0e0e0; background: white;">
              <i
                class="bi bi-clock-history mb-3"
                style="font-size: 2rem; color: #a0a0a0;"></i>
              <h5 class="mb-2" style="font-size: 1.1rem; font-weight: 600;">
                More tests coming soon
              </h5>
              <p class="text-muted" style="font-size: 0.9rem;">
                We are actively building more practice mock tests.
              </p>
            </div>
          </div>
        </ng-container>

        <!-- Mock Test Cards -->
        <ng-container *ngIf="!isLoading && mockTest.length > 0">
          <div
            *ngFor="let test of mockTest; trackBy: trackByTestId"
            class="mock-test-card"
            [attr.data-testid]="'mock-test-card-' + (test.link_id || test._id)"
          >
            <!-- Card Top: Logo & Title & Tag -->
            <div class="card-header-row">
              <div class="logo-box">
                <img
                  alt="company logo"
                  class="object-contain"
                  loading="lazy"
                  [src]="
                    test.company_logo ||
                    'https://ui-avatars.com/api/?name=' +
                      (test.company_name || 'MT')
                  "
                />
              </div>
              <div class="title-column">
                <h3 class="test-title" [title]="test.role_name">{{ test.role_name || "-" }}</h3>
                <span class="tag-badge" [title]="test.company_name">
                  {{ test.company_name || "-" }}
                </span>
              </div>
            </div>

            <!-- Card Middle: 2x2 Details Grid -->
            <div class="details-grid">
              <!-- Questions Count -->
              <div class="detail-item">
                <svg
                  class="w-4 h-4 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                  ></path>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" x2="12.01" y1="17" y2="17"></line>
                </svg>
                <span class="detail-text"
                  >{{ test.totalQuestions }} Questions</span
                >
              </div>

              <!-- Test Duration -->
              <div class="detail-item">
                <svg
                  class="w-4 h-4 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span class="detail-text">{{ getTestDuration(test) }}</span>
              </div>
            </div>

            <!-- Card Bottom: Start Test Button -->
            <!-- <button
              class="btn-start-test"
              [routerLink]="['/mock-test', test.link_id || test._id]"
              [attr.data-testid]="'start-test-' + (test.link_id || test._id)"
            > -->
            <button class="btn-start-test" (click)="startMockTest(test)">
              <span>Start Mock Test</span>
              <svg
                aria-hidden="true"
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M5 12h14"></path>
                <path d="m12 5 7 7-7 7"></path>
              </svg>
            </button>
          </div>
        </ng-container>

        <!-- Fetching More State -->
        <ng-container *ngIf="isFetchingMore">
          <div
            class="mock-test-card flex justify-center items-center py-20 border-none shadow-none bg-transparent"
          >
            <div
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"
            ></div>
          </div>
        </ng-container>
      </div>

      <!-- Right Scroll Arrow -->
      <button
        (click)="scrollRight()"
        *ngIf="(hasMoreTests || !isAtEnd) && mockTest.length > 0"
        class="arrow-btn absolute -right-6 lg:-right-12 top-1/2 -translate-y-1/2 z-10"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2.5"
          viewBox="0 0 24 24"
        >
          <path d="M9 5l7 7-7 7"></path>
        </svg>
      </button>
    </div>
  </div>
</section>
